import { type AxiosPromise } from "axios";
import { FileDropzone } from "components/FileUpload/FileDropzone";
import React from "react";
import { Message, Progress } from "semantic-ui-react";
import { type UploadFilesConfig, type uploadFilesToCoreApi } from "utilities/fileUploadUtils";
import { DeleteDialog, Modal } from "../../../components";
import { NoDataAvailableDiv } from "../../../components/NoDataAvailableDiv";
import EditUploadedFile from "./edituploadedfile";
import UploadedFile from "./uploadedfile";

interface Props {
  isOpen: boolean
  onClose: () => void
  canEdit: boolean

  files: server.dto.File[]
  upload: (files: File[], uploadConfig: UploadFilesConfig) => ReturnType<typeof uploadFilesToCoreApi>
  deleteEndpoint: (id: number) => AxiosPromise<any>
  renameEndpoint: (file: server.dto.File) => AxiosPromise<any>
  onFilesChanged: () => Promise<void>
}

export const ManageMultipleFilesModal = (props: Props) => {
  const [editingFileId, setEditingFileId] = React.useState<number>(null);
  const [deletingFileId, setDeletingFileId] = React.useState<number>(null);
  const [isUploading, setIsUploading] = React.useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = React.useState<number>(0);
  const [uploadError, setUploadError] = React.useState<string>("");

  const onFileNameChanged = async (newFile: server.dto.File) => {
    await props.renameEndpoint(newFile);
    setEditingFileId(null);

    await props.onFilesChanged();
  };

  const onEditClicked = (file: server.dto.File) => {
    setEditingFileId(file.id);
  };

  const onDeleteClicked = (file: server.dto.File) => {
    setDeletingFileId(file.id);
  };

  const onDelete = async () => {
    await props.deleteEndpoint(deletingFileId);
    setDeletingFileId(null);
    await props.onFilesChanged();
  };

  const onDrop = async (files: File[]) => {
    setIsUploading(true);
    setUploadProgress(10);
    setUploadError("");

    try {
      const uploadFilesRes = props.upload(files, {
        onUploadProgress: (e: ProgressEvent) => {
          const percentCompleted = Math.round((e.loaded * 100) / e.total);
          setUploadProgress(percentCompleted);
        },
        fileType: "any"
      });

      if (typeof uploadFilesRes === "string") {
        setIsUploading(false);
        setUploadProgress(0);
        setUploadError(uploadFilesRes);
      } else {
        await uploadFilesRes;
        setIsUploading(false);
        setUploadProgress(0);
        await props.onFilesChanged();
      }
    } catch {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  return <Modal
    title={RESX.File.Files}
    isOpen={props.isOpen}
    width={"small"}
    minHeight={400}
    height={750}
    handleClose={props.onClose}
  >
    <div className="grow1 scrollbox-auto margin-bottom-large">
      {props.files.length === 0 && <NoDataAvailableDiv>{RESX.File.NoFilesUploaded}</NoDataAvailableDiv>}

      {props.files.length > 0 && <div className="list-with-gaps line-layout">
        <div className="list-with-gaps-body">
          {props.files.map(file => editingFileId === file.id
            ? <EditUploadedFile key={file.id} onFileNameChanged={onFileNameChanged} file={file} />
            : <UploadedFile key={file.id} file={file} onEditClicked={onEditClicked} onDeleteClicked={onDeleteClicked} canEdit={props.canEdit} />)}
        </div>
      </div>}
    </div>

    {
      isUploading && <div className="white-text">
        <Progress
          percent={uploadProgress}
          color="green"
          progress={true}
          active={true}>{RESX.File.UploadingFiles}</Progress>
      </div>
    }
    {
      !isUploading && <>
        {uploadError && <Message color="red" className="margin-bottom">
          {uploadError}
        </Message>}
        <div>
          <FileDropzone
            onDrop={onDrop}
            onDropReject={(fileRejections) => {
              setIsUploading(false);
              setUploadProgress(0);
              setUploadError(fileRejections[0].errors[0].message);
            }}
            maxSizeBytes={serverReference.constants.file.maximumTravelFileContentLength}
            iconType="file"
          />
        </div>
      </>
    }
    <DeleteDialog
      isOpen={!!deletingFileId}
      message={RESX.GeneralWarnings.AreYouSure}
      handleClose={() => { setDeletingFileId(null); }}
      onDelete={onDelete}
    />
  </Modal>;
}
